'use client';

import {
  isLanguageEnglish,
  isLanguagePortuguese,
} from '@payments-checkout/shared/helpers/translations/internacional-address.helpers';
import { translationMessages } from '@payments-checkout/shared/helpers/translations/messages.helper';
import moment from 'moment';
import 'moment/locale/en-gb';
import 'moment/locale/pt-br';
import { createContext, PropsWithChildren, useCallback, useContext, useMemo, useState } from 'react';
import { IntlProvider } from 'react-intl';

type localeMessagesType = keyof typeof translationMessages;

const TranslationContext = createContext<{
  currentLocale: localeMessagesType;
  handleChange: (locale: localeMessagesType) => void;
  /* eslint-disable-next-line @typescript-eslint/no-empty-function */
}>({ currentLocale: 'pt', handleChange: () => {} });

function IntlProviderImpl({ children }: PropsWithChildren) {
  const { currentLocale } = useTranslationProvider();

  return (
    <IntlProvider
      locale={currentLocale}
      messages={translationMessages[currentLocale]}
      defaultLocale="pt"
    >
      {children}
    </IntlProvider>
  );
}

export function TranslationProvider({ children }: PropsWithChildren) {
  const [currentLocale, setCurrentLocale] = useState<localeMessagesType>('pt');

  const handleChange = useCallback((e: localeMessagesType) => {
    setCurrentLocale(e);
    if (isLanguagePortuguese(e)) {
      moment.locale('pt-br');
    }
    if (isLanguageEnglish(e)) {
      moment.locale('en-gb');
    }
  }, []);

  const values = useMemo(
    () => ({
      currentLocale,
      handleChange,
    }),
    [currentLocale, handleChange]
  );

  return (
    <TranslationContext.Provider value={values}>
      <IntlProviderImpl>{children}</IntlProviderImpl>
    </TranslationContext.Provider>
  );
}

export function useTranslationProvider() {
  return useContext(TranslationContext);
}
